<template>
  <a-modal
    :title="mode === 'view' ? '查看凭证' : (mode === 'edit' ? '编辑凭证' : '新增凭证')"
    :width="1120"
    :visible="visible"
    :footer="null"
    :maskClosable="false"
    @cancel="close">
    <div v-if="visible" class="voucher-editor" ref="voucherEditorRef">
      <!-- 顶部按钮 -->
      <div class="h-row-flex top-buttons" style="margin-bottom: 12px;">
        <div class="h-col">
          <button v-if="isEdit && !memoryMode" type="button" class="h-btn h-btn-primary" :disabled="saving" @click="save(false)">
            <span>保存并新增</span>
          </button>
          <button v-if="isEdit" type="button" class="h-btn h-btn-primary" :disabled="saving" style="margin-left:8px" @click="save(true)">
            <span>保存</span>
          </button>
        </div>
      </div>

      <!-- 凭证头部信息 -->
      <div class="h-row-flex voucher-head">
        <a-select v-model="form.voucherWordId" style="min-width:70px;width:70px">
          <a-select-option v-for="w in voucherWords" :key="w.id" :value="w.id">{{ w.word }}</a-select-option>
        </a-select>
        <span style="min-width:90px;display:inline-block;text-align:center;">{{ form.voucherNoNum ? form.voucherNoNum + ' 号' : '自动' }}</span>
        <span style="margin-left:12px;">日期：</span>
        <a-date-picker v-model="form.voucherDate" format="YYYY-MM-DD" style="width:140px" />
        <span style="margin-left:12px;">备注：</span>
        <a-input v-model="form.summary" placeholder="请输入备注内容" style="width:300px" />
        <span style="margin-left:12px;">附单据</span>
        <a-input-number v-model="form.receiptNum" :min="0" :precision="0" style="width:70px" />
        <span>张</span>
      </div>

      <div class="voucher-table bg-white-color">
        <table cellspacing="0" cellpadding="0">
          <thead>
            <tr class="bg-white-color">
              <td class="td-zhaoyao-head" style="font-weight:bold">摘要</td>
              <td class="td-kemu-head" style="font-weight:bold">会计科目</td>
              <td class="td-money-head">
                <div style="font-weight:bold">借方金额</div>
                <div class="moneyUnit">
                  <span>亿</span><span>千</span><span>百</span><span>十</span><span>万</span><span>千</span><span>百</span><span>十</span><span>元</span><span>角</span><span class="last">分</span>
                </div>
              </td>
              <td class="td-money-head tdLast">
                <div style="font-weight:bold">贷方金额</div>
                <div class="moneyUnit">
                  <span>亿</span><span>千</span><span>百</span><span>十</span><span>万</span><span>千</span><span>百</span><span>十</span><span>元</span><span>角</span><span class="last">分</span>
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(it, index) in form.items" :key="it.k" class="trDetails">
              <td class="tdZhaoyao tdInput" style="position:relative;">
                <input v-if="isEdit"
                       class="cell-input"
                       v-model="it.summary"
                       placeholder="输入摘要"
                       @focus="openSummaryDrop(it,index,$event)"
                       @blur="handleSummaryBlur(it)"
                />
                <div v-else class="cell-click-display">{{ it.summary || '' }}</div>
                <i v-if="isEdit" class="row-icon row-add" @click="insertRow(index)"><a-icon type="plus-circle" /></i>
              </td>
              <td class="tdKemu tdInput" style="position:relative;">
                <input v-if="isEdit"
                       class="cell-input"
                       v-model="it._subjectDisplayText"
                       placeholder="输入科目编码/名称"
                       @focus="openSubjectDrop(it,index,$event)"
                       @blur="handleSubjectBlur(it)"
                       @input="filterSubject(it)"
                />
                <div v-else class="display subject-display">{{ subjectText(it) }}</div>
                <!-- 数量金额式科目：数量/单价录入（金额=数量*单价） -->
                <div v-if="it._bookFormat === '数量金额式'" class="qty-price-row" @click.stop>
                  <template v-if="isEdit">
                    <span class="qty-label">数量</span><input class="qty-input" type="number" v-model="it.qty" @input="calcAmount(it)" />
                    <span class="qty-label">单价</span><input class="qty-input" type="number" v-model="it.price" @input="calcAmount(it)" />
                  </template>
                  <template v-else>
                    <span class="qty-label">数量 {{ it.qty || '-' }} 单价 {{ it.price || '-' }}</span>
                  </template>
                </div>
                <!-- [FR26.2] 辅助核算内联选择器：按科目 auxiliary_types 动态渲染 N 个 h-select -->
                <div v-if="it.auxTypes && it.auxTypes.length" class="aux-inline-wrap" @click.stop>
                  <div v-for="cat in resolveAuxCategories(it.auxTypes)" :key="cat.id" class="aux-inline-item">
                    <span class="aux-inline-label">{{ cat.name }}</span>
                    <div class="h-select h-select-input-border h-select-no-autosize aux-inline-select"
                         :class="{ 'is-disabled': !isEdit }"
                         @click.stop="toggleAuxSelect(it, cat)">
                      <div class="h-select-show">
                        <div v-if="getAuxSel(it, cat.id) == null" class="h-select-placeholder">{{ isEdit ? '请选择' : '-' }}</div>
                        <div v-else class="h-select-selection">{{ getAuxDisplayName(it, cat.id) }}</div>
                        <i class="h-icon-down">▾</i>
                      </div>
                      <div v-if="isEdit && auxDropOpenKey === it.k + ':' + cat.id" class="h-select-group h-select-single">
                        <!-- 现金流类别：智能候选分组渲染（推荐组在前 + 全量分组） -->
                        <template v-if="cat.name === '现金流'">
                          <template v-for="grp in cashFlowGroups(it, cat)">
                            <div :key="'t-' + grp.label" class="cf-drop-group-title">{{ grp.label }}</div>
                            <div v-for="d in grp.items" :key="grp.label + '-' + d.code" class="h-select-item" @click.stop="setAuxSel(it, cat.id, d.id)">{{ d.code }} {{ d.name }}</div>
                          </template>
                          <div v-if="!cashFlowGroups(it, cat).length" class="cf-drop-group-title">加载中…</div>
                        </template>
                        <!-- 其它辅助类别：原全量明细平铺 -->
                        <template v-else>
                          <div class="h-select-item" v-for="d in (auxDetailsCache[cat.id] || [])" :key="d.id" @click.stop="setAuxSel(it, cat.id, d.id)">{{ d.name }}</div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td class="tdJieFang tdInput">
                <div v-if="it.editField !== 'debit'" class="display displayMoney" :class="{ 'amount-negative': Number(it.debitAmount) < 0 }" @click="startEdit(it, 'debit')">
                  <span v-for="(d, i) in amountDigits(it.debitAmount)" :key="i" class="money-digit" :class="{ last: i === 10 }">{{ d }}</span>
                </div>
                <input v-else v-focus type="text" class="money-edit" v-model="it.debitAmount" @blur="endEdit(it, 'debit')" @keyup.enter="endEdit(it, 'debit')" @click.stop />
              </td>
              <td class="tdLast tdDaiFang tdInput" style="position:relative">
                <div v-if="it.editField !== 'credit'" class="display displayMoney" :class="{ 'amount-negative': Number(it.creditAmount) < 0 }" @click="startEdit(it, 'credit')">
                  <span v-for="(d, i) in amountDigits(it.creditAmount)" :key="i" class="money-digit" :class="{ last: i === 10 }">{{ d }}</span>
                </div>
                <input v-else v-focus type="text" class="money-edit" v-model="it.creditAmount" @blur="endEdit(it, 'credit')" @keyup.enter="endEdit(it, 'credit')" @click.stop />
                <i v-if="isEdit && form.items.length > 1" class="row-icon row-del" @click="delRow(index)"><a-icon type="minus-circle" /></i>
              </td>
            </tr>
            <tr class="trDetails total">
              <td colspan="2" class="spTotal padding-left">合计:</td>
              <td>
                <div class="display displayMoney" :class="{ 'amount-negative': debitTotal < 0 }"><span v-for="(d, i) in amountDigits(debitTotal)" :key="i" class="money-digit" :class="{ last: i === 10 }">{{ d }}</span></div>
              </td>
              <td class="tdLast">
                <div class="display displayMoney" :class="{ 'amount-negative': creditTotal < 0 }"><span v-for="(d, i) in amountDigits(creditTotal)" :key="i" class="money-digit" :class="{ last: i === 10 }">{{ d }}</span></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="voucher-footer">
        <div style="text-align:right;color:#999;padding-right:20px;">制单人：{{ makerName || '当前用户' }}</div>
      </div>
    </div>

    <!--浮层放到a-modal直接子节点 fixed定位 -->
    <div v-if="dropState.showSummary || dropState.showSubject"
         class="drop-panel-root"
         :style="dropState.panelStyle"
         @mousedown.prevent>
      <template v-if="dropState.showSummary">
        <div v-for="opt in summaryFilterList" :key="opt" class="drop-item" @click="selectSummaryItem(dropState.currentRowItem,opt)">
          {{opt}}
        </div>
      </template>
      <template v-if="dropState.showSubject">
        <div v-for="s in subjectFilterList" :key="s.id" class="drop-item" @click="selectSubjectItem(dropState.currentRowItem,s)">
          {{s.subjectCode}} {{s.subjectName}}
        </div>
      </template>
    </div>

  </a-modal>
</template>

<script>
import { voucherGet, voucherSave, subjectLeafList, auxiliaryCategoryList, auxiliaryDetailList, voucherWordList, cashFlowCandidates } from './api'
import { getDicts } from '@/api/api'
import moment from 'moment'

export default {
  name: 'VoucherEditor',
  directives: {
    focus: { inserted(el) { el.focus() } }
  },
  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String, default: 'add' },
    accountSetId: { type: Number, default: null },
    period: { type: String, default: '' },
    voucherId: { type: Number, default: null },
    // 内存模式：不调 voucherSave 落库，保存改为 emit('confirm', {items}) 交宿主页面暂存
    memoryMode: { type: Boolean, default: false },
    // 内存模式行预填：[{subjectId, summary, debitAmount, creditAmount}]
    prefillItems: { type: Array, default: null }
  },
  data() {
    return {
      saving: false,
      // 凭证当前状态（'0'可编辑；'1'已审核/'9'审核中只读）
      voucherStatus: '0',
      voucherWords: [],
      leafSubjects: [],
      auxCategories: [],
      summaryOptions: [],
      // [FR26] 辅助核算内联选择器状态：auxDropOpenKey 单一下拉展开键（rowKey:categoryId）；auxDetailsCache 类别明细缓存（categoryId -> detail[]）
      auxDropOpenKey: null,
      auxDetailsCache: {},
      // 现金流智能候选缓存（键：direction + '|' + counterparts CSV → {matched, groups}）
      cashFlowCandCache: {},
      dropState:{
        showSummary:false,
        showSubject:false,
        curItemKey:null,
        blurTimer:null,
        currentRowItem: null,
        panelStyle: { top:'0px', left:'0px', width:'200px' }
      },
      summaryFilterList:[],
      subjectFilterList:[],
      modalBody: null,
      form: {
        voucherWordId: undefined,
        voucherNoNum: undefined,
        voucherNoStr: '',
        voucherDate: moment(),
        summary: '',
        items: []
      }
    }
  },
  computed: {
    isEdit() { return this.mode !== 'view' && this.voucherStatus === '0' },
    /** 当前凭证字文本（提交 word，后端按字+code 取号） */
    currentWord() {
      const w = (this.voucherWords || []).find(x => x.id === this.form.voucherWordId)
      return w ? w.word : '记'
    },
    debitTotal() { return (this.form.items || []).reduce(function(s, i) { return s + (Number(i.debitAmount) || 0); }, 0) },
    creditTotal() { return (this.form.items || []).reduce(function(s, i) { return s + (Number(i.creditAmount) || 0); }, 0) },
    makerName() {
      const u = this.$store.getters.userInfo || {}
      return u.realname || u.username || ''
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.reset()
        this.$nextTick(()=>{
          // 去掉 ?. 可选链，兼容旧babel
          const refEl = this.$refs.voucherEditorRef
          if(refEl && refEl.$el){
            var body = refEl.$el.closest('.ant-modal-body')
            if(body){
              this.modalBody = body
              this.modalBody.addEventListener('scroll',this.onModalScroll)
            }
          }
        })
      }else{
        this.clearDropTimer()
        this.dropState.showSummary = false
        this.dropState.showSubject = false
        this.auxDropOpenKey = null
        if(this.modalBody){
          this.modalBody.removeEventListener('scroll',this.onModalScroll)
          this.modalBody = null
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onModalScroll(){
      this.clearDropTimer()
      this.dropState.showSummary = false
      this.dropState.showSubject = false
      this.auxDropOpenKey = null
    },
    /** [FR26] 点击页面空白处关闭辅助核算下拉 */
    onDocClick() {
      this.auxDropOpenKey = null
    },
    close() { this.$emit('update:visible', false) },
    reset() {
      this.clearDropTimer();
      this.dropState.showSummary = false;
      this.dropState.showSubject = false;
      this.dropState.curItemKey = null;
      this.dropState.currentRowItem = null;
      this.auxDropOpenKey = null;
      this.cashFlowCandCache = {};

      this.form = { voucherWordId: undefined, voucherNoNum: undefined, voucherNoStr: '', voucherDate: moment(), summary: '', receiptNum: 0, items: [] }
      this.voucherStatus = '0'
      this.loadBase()
      if (this.memoryMode) {
        // 内存模式：外部预填行（结转卡片测算行），不查库；科目文本由 loadBase 的 leafSubjects 回调统一富化
        if (this.prefillItems && this.prefillItems.length) {
          this.form.items = this.prefillItems.map(function(p) {
            return {
              k: 'pf' + Date.now() + Math.random(),
              subjectId: p.subjectId,
              summary: p.summary || '',
              debitAmount: p.debitAmount,
              creditAmount: p.creditAmount,
              qty: undefined,
              price: undefined,
              editField: null,
              auxTypes: [],
              auxItems: [],
              auxSelections: {},
              auxSummary: '',
              _subjectDisplayText: '',
              _direction: '',
              _bookFormat: ''
            }
          })
        } else if (this.mode !== 'view') {
          for (let i = 0; i < 4; i++) this.addRow()
        }
      } else if ((this.mode === 'edit' || this.mode === 'view') && this.voucherId) {
        voucherGet({ id: this.voucherId }).then((res) => {
          if (!res || !res.success || !res.data) { this.$message.error('加载凭证失败'); return }
          const v = res.data
          this.voucherStatus = v.status || '0'
          const no = v.voucherNo || ''
          const idx = no.lastIndexOf('-')
          this.form = {
            id: v.id,
            voucherNoNum: v.code || (idx >= 0 ? (Number(no.substring(idx + 1)) || undefined) : undefined),
            voucherNoStr: v.voucherNo || '',
            voucherDate: v.voucherDate ? moment(String(v.voucherDate)) : moment(),
            summary: v.remark || '',
            receiptNum: v.receiptNum || 0,
            items: (v.items || []).map(it => ({
              k: 'e' + it.id + Math.random(),
              subjectId: it.subjectId,
              summary: it.summary || '',
              debitAmount: it.debitAmount,
              creditAmount: it.creditAmount,
              qty: it.qty,
              price: it.price,
              editField: null,
              auxTypes: [],
              auxItems: (it.auxItems || []).map(a => ({ categoryId: a.categoryId, detailId: a.detailId, categoryName: a.categoryName, detailName: a.detailName })),
              auxSelections: {},
              _subjectDisplayText:'',
              _direction: '',
              _bookFormat: ''
            }))
          }
          // 【FR26】收集所有涉及的 categoryId，预拉取明细存入 auxDetailsCache，保证内联选择器选项 + auxSummary 正确
          var catIds = {}
          this.form.items.forEach(function(it){ (it.auxItems || []).forEach(function(a){ if(a && a.categoryId) catIds[a.categoryId] = true }) })
          var idList = Object.keys(catIds)
          var _this = this
          var done = 0
          var afterAll = function() {
            _this.form.items.forEach(function(it) {
              var s = _this.leafSubjects.find(function(x){ return x.id === it.subjectId })
              it.auxTypes = (s && s.auxiliaryTypes) ? s.auxiliaryTypes.split(',') : []
              if(s){
                it._subjectDisplayText = s.subjectCode + ' ' + s.subjectName
                it._direction = s.direction || ''
                it._bookFormat = s.bookFormat || ''
              }
              // [FR26.3] 由 auxItems 回填 auxSelections（内联选择器 v-model 数据源）
              var sel = {}
              ;(it.auxItems || []).forEach(function(a){ if(a && a.categoryId) sel[a.categoryId] = a.detailId })
              _this.$set(it, 'auxSelections', sel)
              _this.rebuildAuxSummary(it)
            })
          }
          if (!idList.length) { afterAll(); return }
          idList.forEach(function(cid){
            var cidNum = Number(cid) || cid
            if (!_this.auxDetailsCache[cidNum]) _this.$set(_this.auxDetailsCache, cidNum, [])
            auxiliaryDetailList({ categoryId: cidNum }).then(function(res){
              if (res && res.success) _this.$set(_this.auxDetailsCache, cidNum, res.data || [])
              done++
              if (done >= idList.length) afterAll()
            }).catch(function(){ done++; if (done >= idList.length) afterAll() })
          })
        })
      } else {
        for (let i = 0; i < 4; i++) this.addRow()
      }
    },
    loadBase() {
      voucherWordList({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success) {
          this.voucherWords = res.data || []
          if (!this.form.voucherWordId && this.voucherWords.length) this.form.voucherWordId = this.voucherWords[0].id
        }
      })
      subjectLeafList({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success) {
          this.leafSubjects = res.data || []
          this.enrichItems()
        }
      })
      auxiliaryCategoryList({ accountSetId: this.accountSetId }).then((res) => { if (res && res.success) this.auxCategories = res.data || [] })
      getDicts('voucher_summary').then((res) => {
        if (res && res.success && res.data) this.summaryOptions = res.data.map(d => d.dictLabel).filter(Boolean)
      }).catch(() => { this.summaryOptions = [] })
    },
    newRow() {
      return {
        k: 'it' + Date.now() + Math.random(),
        subjectId: undefined,
        debitAmount: undefined,
        creditAmount: undefined,
        summary: undefined,
        qty: undefined,
        price: undefined,
        editField: null,
        auxTypes: [],
        auxItems: [],
        auxSelections: {},
        auxSummary: '',
        _subjectDisplayText: '',
        _direction: '',
        _bookFormat: ''
      }
    },
    addRow() { this.form.items.push(this.newRow()) },
    /** 科目列表就绪后统一富化分录行（科目文本/方向/账页格式/辅助类型；预填行与编辑加载行共用） */
    enrichItems() {
      var _this = this
      if (!this.leafSubjects || !this.leafSubjects.length) return
      this.form.items.forEach(function(it) {
        if (!it.subjectId) return
        var s = _this.leafSubjects.find(function(x) { return x.id === it.subjectId })
        if (s) {
          it._subjectDisplayText = s.subjectCode + ' ' + s.subjectName
          it._direction = s.direction || ''
          it._bookFormat = s.bookFormat || ''
          it.auxTypes = s.auxiliaryTypes ? s.auxiliaryTypes.split(',') : []
        }
      })
    },
    insertRow(index) { this.form.items.splice(index + 1, 0, this.newRow()) },
    delRow(index) {
      if (this.form.items.length <= 1) return
      this.form.items.splice(index, 1)
    },
    amountDigits(amount) {
      const n = Number(amount) || 0
      const neg = n < 0
      const v = Math.round(Math.abs(n) * 100)
      const s = v.toString().padStart(11, '0')
      const out = []
      let started = false
      for (const ch of s.slice(0, 9)) {
        if (!started && ch === '0') out.push('')
        else { started = true; out.push(ch) }
      }
      out.push(s.slice(9, 10), s.slice(10, 11))
      if (neg) {
        const idx = out.findIndex(x => x !== '')
        if (idx === 0) out[0] = '-'
        else if (idx > 0) out[idx - 1] = '-'
      }
      return out
    },
    startEdit(item, field) {
      if (!this.isEdit) return
      item.editField = field
    },
    /** 数量金额式：金额 = 数量 × 单价，自动写入余额方向对应列 */
    calcAmount(it) {
      const qty = parseFloat(it.qty)
      const price = parseFloat(it.price)
      if (isNaN(qty) || isNaN(price)) return
      const amount = Math.round(qty * price * 100) / 100
      if (it._direction === '贷') {
        it.creditAmount = amount
        it.debitAmount = it.debitAmount || 0
      } else {
        it.debitAmount = amount
        it.creditAmount = it.creditAmount || 0
      }
    },
    endEdit(item, field) {
      const v = parseFloat(item[field])
      item[field] = isNaN(v) ? 0 : Math.round(v * 100) / 100
      item.editField = null
    },
    subjectText(it) {
      const s = this.leafSubjects.find(x => x.id === it.subjectId)
      if (!s) return ''
      return (s.subjectCode || '') + ' ' + s.subjectName + (s.auxiliaryTypes ? '_' + s.auxiliaryTypes : '')
    },

    getDropPanelPos(inputDom){
      const rect = inputDom.getBoundingClientRect();
      const panelMaxHeight = 220;
      const spaceDown = window.innerHeight - rect.bottom;
      const style = {
        left: rect.left + 'px',
        width: rect.width + 'px',
      }
      if(spaceDown >= panelMaxHeight){
        style.top = rect.bottom + 'px';
        style.bottom = 'auto';
      }else{
        style.bottom = (window.innerHeight - rect.top) + 'px';
        style.top = 'auto';
      }
      return style;
    },

    openSummaryDrop(item,index,event){
      this.clearDropTimer();
      this.dropState.showSubject = false;
      this.dropState.showSummary = true;
      this.dropState.curItemKey = item.k;
      this.dropState.currentRowItem = item;
      this.summaryFilterList = [...this.summaryOptions];
      this.dropState.panelStyle = this.getDropPanelPos(event.target);
    },
    handleSummaryBlur(){
      this.clearDropTimer();
      this.dropState.blurTimer = setTimeout(()=>{
        this.dropState.showSummary = false;
        this.dropState.curItemKey = null;
        this.dropState.currentRowItem = null;
      },220)
    },
    selectSummaryItem(item,opt){
      this.clearDropTimer();
      item.summary = opt;
      this.dropState.showSummary = false;
      this.dropState.curItemKey = null;
      this.dropState.currentRowItem = null;
    },

    openSubjectDrop(item,index,event){
      this.clearDropTimer();
      this.dropState.showSummary = false;
      this.dropState.showSubject = true;
      this.dropState.curItemKey = item.k;
      this.dropState.currentRowItem = item;
      this.subjectFilterList = [...this.leafSubjects];
      this.dropState.panelStyle = this.getDropPanelPos(event.target);
    },
    filterSubject(item){
      const kw = (item._subjectDisplayText||'').toLowerCase().trim()
      if(!kw){
        this.subjectFilterList = [...this.leafSubjects]
      }else{
        this.subjectFilterList = this.leafSubjects.filter(function(s){
          return s.subjectCode.toLowerCase().indexOf(kw) > -1 || s.subjectName.toLowerCase().indexOf(kw) > -1
        })
      }
    },
    handleSubjectBlur(){
      this.clearDropTimer();
      this.dropState.blurTimer = setTimeout(()=>{
        this.dropState.showSubject = false;
        this.dropState.curItemKey = null;
        this.dropState.currentRowItem = null;
      },220)
    },
    selectSubjectItem(item,s){
      this.clearDropTimer();
      item.subjectId = s.id;
      item._subjectDisplayText = s.subjectCode + ' ' + s.subjectName;
      item.auxTypes = (s && s.auxiliaryTypes) ? s.auxiliaryTypes.split(',') : []
      // [FR26.1] 切换科目时重置辅助选择，按需拉取明细选项进 auxDetailsCache
      this.$set(item, 'auxSelections', {})
      this.$set(item, 'auxItems', [])
      this.$set(item, 'auxSummary', '')
      item._direction = s.direction || ''
      item._bookFormat = s.bookFormat || ''
      this.dropState.showSubject = false;
      this.dropState.curItemKey = null;
      this.dropState.currentRowItem = null;
      if (item.auxTypes && item.auxTypes.length) {
        this.preloadAuxDetails(item.auxTypes)
      } else if (this.isEdit) {
        // 无辅助核算：按余额方向自动定位金额录入列（借→借方，贷→贷方）
        this.autoFocusAmount(item)
      }
    },
    /** 科目余额方向自动定位借贷列 */
    autoFocusAmount(item) {
      if (item._direction === '借' && (item.debitAmount === null || item.debitAmount === '')) {
        this.startEdit(item, 'debit')
      } else if (item._direction === '贷' && (item.creditAmount === null || item.creditAmount === '')) {
        this.startEdit(item, 'credit')
      }
    },
    clearDropTimer(){
      if(this.dropState.blurTimer){
        clearTimeout(this.dropState.blurTimer)
        this.dropState.blurTimer = null
      }
    },

    /** [FR26.1] 按 auxiliaryTypes 类别名解析为类别对象数组（过滤未匹配项） */
    resolveAuxCategories(auxTypeNames) {
      var _this = this
      return (auxTypeNames || []).map(function(name){ return _this.auxCategories.find(function(c){ return c.name === name }) }).filter(function(c){ return c })
    },
    /** [FR26.1] 按需拉取类别明细进 auxDetailsCache（已缓存则跳过） */
    preloadAuxDetails(auxTypeNames) {
      var _this = this
      var cats = this.resolveAuxCategories(auxTypeNames)
      cats.forEach(function(c) {
        if (_this.auxDetailsCache[c.id]) return
        _this.$set(_this.auxDetailsCache, c.id, [])
        auxiliaryDetailList({ categoryId: c.id }).then(function(res){
          if (res && res.success) _this.$set(_this.auxDetailsCache, c.id, res.data || [])
        }).catch(function(){})
      })
    },
    /** [FR26.2] 展开/收起某行某类别辅助下拉（单一下拉键）；现金流类别展开时拉取智能候选 */
    toggleAuxSelect(it, cat) {
      const key = it.k + ':' + cat.id
      if (this.auxDropOpenKey === key) { this.auxDropOpenKey = null; return }
      this.auxDropOpenKey = key
      if (cat.name === '现金流' && this.isEdit) {
        this.preloadAuxDetails([cat.name])
        this.loadCashFlowCandidates(it)
      }
    },
    /** 现金流候选请求参数：direction=本行借贷方向（借方有额→借/贷方有额→贷）；counterparts=其它分录科目编码前4位去重 CSV */
    cashFlowCandParams(it) {
      let direction = ''
      if ((Number(it.debitAmount) || 0) !== 0) direction = '借'
      else if ((Number(it.creditAmount) || 0) !== 0) direction = '贷'
      const seen = {}
      const list = []
      ;(this.form.items || []).forEach((o) => {
        if (o === it || !o.subjectId) return
        const s = this.leafSubjects.find((x) => x.id === o.subjectId)
        const code = s && s.subjectCode ? String(s.subjectCode).substring(0, 4) : ''
        if (code && !seen[code]) { seen[code] = true; list.push(code) }
      })
      const csv = list.join(',')
      return { direction: direction, counterparts: csv, cacheKey: direction + '|' + csv }
    },
    /** 拉取现金流智能候选（按 direction|counterparts 缓存，避免重复请求） */
    loadCashFlowCandidates(it) {
      const p = this.cashFlowCandParams(it)
      if (this.cashFlowCandCache[p.cacheKey]) return
      cashFlowCandidates({ accountSetId: this.accountSetId, direction: p.direction, counterparts: p.counterparts }).then((res) => {
        if (res && res.success && res.data) this.$set(this.cashFlowCandCache, p.cacheKey, res.data)
      }).catch(() => {})
    },
    /** 现金流类别下拉分组数据：matched→"推荐"组在前、groups 全量分组在后；候选项按 code→id 关联 auxDetailsCache 明细（未含则跳过） */
    cashFlowGroups(it, cat) {
      const cand = this.cashFlowCandCache[this.cashFlowCandParams(it).cacheKey]
      if (!cand) return []
      const codeId = {}
      ;(this.auxDetailsCache[cat.id] || []).forEach((d) => {
        if (d.code == null) return
        const c = String(d.code).trim()
        if (c) codeId[c] = d.id
      })
      const toItems = (items) => (items || []).map((m) => {
        const id = codeId[String(m.code)]
        return id != null ? { code: m.code, name: m.name, id: id } : null
      }).filter(Boolean)
      const groups = []
      if (cand.matched && cand.matched.length) {
        const items = toItems(cand.matched)
        if (items.length) groups.push({ label: '推荐', items: items })
      }
      ;(cand.groups || []).forEach((g) => {
        const items = toItems(g.items)
        if (items.length) groups.push({ label: g.label, items: items })
      })
      return groups
    },
    /** [FR26.2] 读取某行某类别已选 detailId */
    getAuxSel(it, categoryId) {
      return (it.auxSelections && it.auxSelections[categoryId] != null) ? it.auxSelections[categoryId] : null
    },
    /** [FR26.2] 由 categoryId+detailId 查缓存明细名（展示用） */
    getAuxDetailName(categoryId, detailId) {
      if (detailId == null) return ''
      var list = this.auxDetailsCache[categoryId] || []
      var d = list.find(function(x){ return x.id === detailId })
      return d ? d.name : ''
    },
    /** [13.7] 优先取后端回填的 detailName（账户改名后刷新显示新名），回退缓存明细名 */
    getAuxDisplayName(it, categoryId) {
      var did = this.getAuxSel(it, categoryId)
      if (did == null) return ''
      var prev = (it.auxItems || []).find(function(a){ return a.categoryId === categoryId && a.detailId === did })
      if (prev && prev.detailName) return prev.detailName
      return this.getAuxDetailName(categoryId, did)
    },
    /** [FR26.2] 选中辅助项：写入 auxSelections 并重建 auxItems/auxSummary */
    setAuxSel(it, categoryId, detailId) {
      if (!it.auxSelections) this.$set(it, 'auxSelections', {})
      this.$set(it.auxSelections, categoryId, detailId)
      this.auxDropOpenKey = null
      this.rebuildAuxSummary(it)
    },
    /** [FR26] 由 it.auxSelections + 缓存明细 重建 auxItems 与 auxSummary（保留后端回填的 detailName） */
    rebuildAuxSummary(it) {
      var _this = this
      var cats = this.resolveAuxCategories(it.auxTypes || [])
      var prevItems = it.auxItems || []
      var auxItems = []
      var names = []
      var details = []
      cats.forEach(function(c) {
        var did = (it.auxSelections && it.auxSelections[c.id] != null) ? it.auxSelections[c.id] : null
        if (did != null) {
          var prev = prevItems.find(function(p){ return p.categoryId === c.id && p.detailId === did })
          var dName = (prev && prev.detailName) ? prev.detailName : _this.getAuxDetailName(c.id, did)
          auxItems.push({ categoryId: c.id, detailId: did, categoryName: c.name, detailName: dName })
          if (dName) { names.push(c.name); details.push(dName) }
        }
      })
      this.$set(it, 'auxItems', auxItems)
      var head = names.length ? names.join(',') : ''
      var tail = details.length ? details.join(' ') : ''
      this.$set(it, 'auxSummary', [head, tail].filter(Boolean).join(' '))
    },
    save(closeAfter) {
      const filledItems = this.form.items.filter(i =>
        i.subjectId || (Number(i.debitAmount) || 0) !== 0 || (Number(i.creditAmount) || 0) !== 0 || i.summary)
      for (const i of filledItems) {
        if (!i.subjectId) {
          this.$message.warning('存在未选择会计科目的分录')
          return
        }
        const debit = Number(i.debitAmount) || 0
        const credit = Number(i.creditAmount) || 0
        if (debit === 0 && credit === 0) {
          this.$message.warning('存在金额为 0 的分录')
          return
        }
        // [FR26.3] 科目声明了 auxiliary_types 则每个类别必须选择辅助项
        if (i.auxTypes && i.auxTypes.length) {
          const cats = this.resolveAuxCategories(i.auxTypes)
          const missing = cats.find(c => !i.auxSelections || i.auxSelections[c.id] == null)
          if (missing) {
            this.$message.warning(`存在未选择的辅助核算「${missing.name}」`)
            return
          }
        }
      }
      const items = filledItems.map(i => ({
        subjectId: i.subjectId,
        summary: i.summary || this.form.summary,
        debitAmount: Number(i.debitAmount) || 0,
        creditAmount: Number(i.creditAmount) || 0,
        qty: i.qty === '' || i.qty === undefined ? null : Number(i.qty),
        price: i.price === '' || i.price === undefined ? null : Number(i.price),
        auxiliaryTitle: i.auxSummary || null,
        auxItems: (i.auxItems || []).filter(a => a.categoryId && a.detailId)
      }))
      if (!items.length) {
        this.$message.warning('请至少录入一条分录')
        return
      }
      const d = this.debitTotal
      const c = this.creditTotal
      if (d === 0) {
        this.$message.warning('凭证金额不能全部为 0')
        return
      }
      if (Math.abs(d - c) > 0.0001) {
        this.$message.warning(`借贷不平衡：借方=${d} 贷方=${c}`)
        return
      }
      if (this.memoryMode) {
        // 内存模式：不落库，分录交回宿主页面暂存（结账时才生成）
        this.$emit('confirm', { items })
        this.close()
        return
      }
      const voucherNo = this.form.id ? this.form.voucherNoStr : null
      this.saving = true
      voucherSave({
        accountSetId: this.accountSetId,
        id: this.form.id,
        voucherNo,
        word: this.currentWord,
        voucherDate: this.form.voucherDate ? this.form.voucherDate.format('YYYY-MM-DD') : '',
        period: this.period,
        remark: this.form.summary,
        receiptNum: this.form.receiptNum || 0,
        items
      }).then(res => {
        if (res && res.success) {
          this.$message.success('保存成功')
          this.$emit('saved')
          if (closeAfter) {
            this.close()
          } else {
            this.form = { voucherWordId: this.form.voucherWordId, voucherNoNum: undefined, voucherNoStr: '', voucherDate: this.form.voucherDate, summary: '', receiptNum: 0, items: [] }
            for (let i = 0; i < 4; i++) this.addRow()
          }
        } else {
          this.$message.error(res.message || '保存失败')
        }
        this.saving = false
      }).catch(() => {
        this.saving = false
        this.$message.error('保存失败')
      })
    }
  }
}
</script>

<style scoped>
/* 数量金额式：科目单元格内数量/单价录入 */
.qty-price-row { display: flex; align-items: center; padding: 0 4px 2px 4px; font-size: 12px; color: #666; }
.qty-label { white-space: nowrap; margin: 0 2px; }
.qty-input { width: 64px; height: 20px; line-height: 20px; border: 1px solid #e2e2e2; border-radius: 2px; padding: 0 4px; font-size: 12px; outline: none; }
.qty-input:focus { border-color: rgb(24,144,255); }
.h-row-flex { display: flex; flex-direction: row; flex-wrap: wrap; }
.h-col { flex: 0 0 auto; }
.voucher-head { align-items: center; margin-bottom: 10px; }
.voucher-head > * { margin-right: 6px; }
.h-btn { outline: none; padding: 8px 15px; font-family: inherit; display: inline-block; border-radius: 2px; color: inherit; font-size: 13px; line-height: 1; box-sizing: border-box; cursor: pointer; transition: all .2s; border: 1px solid #d3d3d3; box-shadow: 0 1px 1px 0 #eee; background-color: #fff; }
.h-btn[disabled] { cursor: not-allowed; background-color: #f5f5f5; border-color: #d3d3d3; color: #999; }
.h-btn.h-btn-primary { background: rgb(24,144,255); border-color: rgb(24,144,255); color: #fff; }

.voucher-table { position: relative; font-size: 12px; width: 100%; padding: 10px 24px; }
.voucher-table table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}
.voucher-table .tdLast { border-right: 1px solid #dadada; }

.td-zhaoyao-head, .tdZhaoyao { width: 208px; }
.td-kemu-head, .tdKemu { width: 308px; }
.td-money-head, .tdJieFang, .tdDaiFang { width: 221px; }

.voucher-table table thead td,
.voucher-table table tbody td {
  border-left: 1px solid #dadada;
  border-top: 1px solid #dadada;
  border-bottom: 1px solid #dadada;
  padding: 0;
  vertical-align: middle;
  height: 60px;
  box-sizing: border-box;
}
.voucher-table table thead tr td {
  text-align: center;
  line-height: 30px;
}

.cell-input{
  width:100%;
  height:60px;
  border:none;
  outline:none;
  padding:0 6px;
  font-size:13px;
  background:transparent;
  box-sizing:border-box;
}
.cell-input:focus{
  background:#fff;
}

.drop-panel-root{
  position: fixed;
  max-height:220px;
  min-height:0;
  overflow-y:auto;
  overflow-x:hidden;
  background:#ffffff;
  border:1px solid #ccc;
  z-index:1200 !important;
}
.drop-item{
  padding:6px 8px;
  cursor:pointer;
  font-size:13px;
}
.drop-item:hover{
  background:#e8f4ff;
}

.voucher-table .moneyUnit {
  border-top: 1px solid #dadada;
  height: 30px;
  width: 221px;
  margin: 0 auto;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANsAAAAUCAMAAAAz47gTAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA9QTFRF6enpp9n17bm55ubmvJyc+JGLrgAAAAV0Uk5T/////wD7tg5TAAAAQklEQVR42uzPsQ0AIAwDsBb4/2YOYCkCqYszRsngWGdmscum7yju4qHLpu/47GBjY2NjY2NjY2NjY2NjY7u3bQEGAIG/Qbbiioa2AAAAAElFTkSuQmCC);
  display: grid;
  grid-template-columns: repeat(11, 19px);
  gap:1px;
}
.voucher-table .moneyUnit span {
  height:100%;
  background-color: #fff;
  text-align: center;
  font-size: 12px;
  line-height: 30px;
}

.voucher-table .body .trDetails .spTotal {
  text-align: left;
  vertical-align: middle;
  font-weight: 700;
  padding-left: 6px;
}

.cell-click-display {
  height: 60px;
  line-height: 60px;
  padding:0 6px;
  cursor: pointer;
  text-align:left;
  box-sizing: border-box;
  white-space: nowrap;
  overflow:hidden;
  text-overflow: ellipsis;
}
.cell-click-display:hover {
  background-color:#f7f8fa;
}

.voucher-table .displayMoney {
  width: 221px;
  height: 60px;
  margin: 0 auto;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANsAAAAUCAMAAAAz47gTAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA9QTFRF6enpp9n17bm55ubmvJyc+JGLrgAAAAV0Uk5T/////wD7tg5TAAAAQklEQVR42uzPsQ0AIAwDsBb4/2YOYCkCqYszRsngWGdmscum7yju4qHLpu/47GBjY2NjY2NjY2NjY2NjY7u3bQEGAIG/Qbbiioa2AAAAAElFTkSuQmCC);
  font-weight: 700;
  font-size: 14px;
  font-family: tahoma, serif;
  display: grid;
  grid-template-columns: repeat(11,19px);
  gap:1px;
}
.displayMoney .money-digit {
  height: 100%;
  background-color: #fff;
  text-align: center;
  line-height: 60px;
}
.displayMoney.amount-negative { color: #f5222d; }

.voucher-table .subject-display { font-weight: 700; }
.money-edit {
  width: 100%;
  height: 60px;
  border: 0;
  outline: 0;
  text-align: right;
  padding: 0 4px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
}
.bg-white-color { background-color: #fff !important; }

.row-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .2s;
  color: #1890ff;
  z-index: 10;
}
.row-add { left: -26px; }
.row-del { right: -26px; }
.trDetails { position: relative; }
.voucher-table .trDetails:hover .row-icon { opacity: 1; }

/* ========== [FR26] 辅助核算内联 h-select ========== */
.aux-inline-wrap { padding: 2px 4px 4px 4px; }
.aux-inline-item { display: flex; align-items: center; margin-top: 3px; }
.aux-inline-label { font-size: 12px; color: #666; white-space: nowrap; margin-right: 4px; min-width: 38px; text-align: right; }
.aux-inline-select { flex: 1; min-width: 0; }
.aux-inline-select .h-select-show { min-height: 28px; }
.aux-inline-select .h-select-placeholder,
.aux-inline-select .h-select-selection { line-height: 24px; height: 24px; font-size: 12px; }
.aux-inline-select.is-disabled { cursor: not-allowed; }
.aux-inline-select.is-disabled .h-select-show { cursor: not-allowed; background-color: #f7f7f7; }
.aux-inline-select.is-disabled .h-icon-down { color: #bbb; }

/* h-select 通用样式（与 VoucherList 对齐：placeholder #787878，hover #f8f8f8） */
.h-select { box-sizing: border-box; vertical-align: middle; position: relative; }
.h-select-placeholder { color: #787878; line-height: 26px; height: 26px; padding-left: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.h-select-selection { line-height: 26px; height: 26px; padding-left: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.h-select-show { padding-right: 25px !important; line-height: 1.5; display: flex; flex-wrap: wrap; }
.h-select-show > .h-icon-down { color: #666; position: absolute; right: 8px; top: 50%; height: 10px; font-size: 12px; line-height: 12px; transform: translateY(-50%) scale(.8); transition: transform .2s; font-style: normal; }
.h-select-show:hover > .h-icon-down { color: #1890ff; }
.h-select-input-border .h-select-show { min-height: 32px; cursor: default; background-color: #fff; border-radius: 2px; border: 1px solid #eee; vertical-align: top; transition: all .2s linear; padding: 2px 2px 0 2px; font-size: 13px; position: relative; box-sizing: border-box; }
.h-select-input-border .h-select-show:hover { border-color: #4ba9ff; }
.h-select-group { outline: none; user-select: none; font-size: 13px; background-color: #fff; padding: 5px 0; }
.h-select.h-select-input-border .h-select-group { position: absolute; left: 0; right: 0; top: 100%; margin-top: 2px; z-index: 100; border: 1px solid #eee; border-radius: 2px; box-shadow: 0 5px 10px rgba(0, 0, 0, .1); max-height: 200px; overflow-y: auto; }
.h-select-group .h-select-item { padding: 7px 10px; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.h-select-group .h-select-item:hover { background-color: #f8f8f8; }
/* 现金流候选分组标题 */
.cf-drop-group-title { padding: 6px 10px 2px 10px; font-size: 12px; color: #999; font-weight: 600; }
</style>